import { Shipment as EasypostShipment } from "@easypost/api";
import { sumBy } from "lodash";
import { Order } from "./order";
import { PaymentIntentItem } from "./payment-intent";
import type { PickupLocation } from "./return-flow";
import { Settlement } from "./return-flow";
import { Address, ExchangeGroup, Team } from "./team";
import { TimelineEvent } from "./timeline";

export interface CustomerSummary {
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
}

export interface Fulfillment {
  status: string;
}

export interface ReturnsFacets {
  total: number;
  byStatus: { [T in Status]: number };
}

export interface NeedsActionCounts {
  returns: number;
  claims: number;
}

export type Status =
  | "open"
  | "in_transit"
  | "cancelled"
  | "closed"
  | "delivered"
  | "needs_review"
  | "complete"
  | "rejected"
  | "flagged";

export type Provision = "processed" | "instant";
export type ReturnType = "return" | "claim";
export type InstantRefundStatus =
  | "pending"
  | "payment_failed"
  | "paid"
  | "processed"
  | "expired";

export type ReturnAddress = {
  name?: string;
  address1: string;
  address2?: string;
  city: string;
  country?: string;
  country_code?: string;
  province?: string;
  province_code?: string;
  zip?: string;
  latitude?: string;
  longitude?: string;
  location?: string; // id of location in Redo for return location
  first_name?: string;
  last_name?: string;
  company?: string;
  email?: string;
  phone: string;
};

type ErrorTag = "label" | "instant-refund" | "pickup";

export interface ReturnWarning {
  code: string;
  type: ErrorTag;
  customerMessage?: string;
  systemMessage?: string;
  error?: string;
}

export interface Return {
  _id: string;
  advancedExchangeItems: ExchangeItem[];
  cancelled_products: any[];
  createdAt: string;
  customer: CustomerSummary;
  draftOrder?: any;
  exchangeOrder: any[];
  exchanges: any[];
  form_label: string;
  fulfillment: Fulfillment;
  giftCards: any[];
  id: string;
  options: { sendNotifications: boolean; waiveDraftFee?: boolean };
  orders: OrderSummary[];
  postage_label: string;
  products: Product[];
  provision: Provision;
  questionAnswers: LineItemAnswers[];
  refunds: any[];
  shipment: Shipment;
  shipments?: Shipment[];
  shipmentGroups?: ShipmentGroup[];
  some_processed: boolean;
  status: string;
  storedCredits: any[];
  team: Team;
  timeline: TimelineEvent[];
  totals: Totals;
  type: ReturnType;
  updatedAt: string;
  address?: ReturnAddress;
  shipping_address?: ReturnAddress;
  newOrderAddress?: ReturnAddress;
  merchant_address?: ReturnAddress;
  new_order_taxes: string;
  labelDeductedFromCredit: boolean;
  shippingFeeToDeduct?: number;
  draftOrderId: number;
  line_item_id: string;
  stripe: {
    payment_token: string;
    customer_id: string;
  };
  discount_allocations: DiscountAllocation[];
  originalReturnOptions?: {
    advancedExchangeItems?: ExchangeItem[];
    new_order_taxes?: string;
    discount_allocations: DiscountAllocation[];
  };
  paymentIntents: PaymentIntentItem[];
  processAt: {
    exchange: Date;
    storeCredit: Date;
    refund: Date;
  };
  rejectType?: string;
  sentInvoice?: boolean;
  completedAt?: Date;
  instantRefund?: {
    astra_user_id: string;
    astraRoutine: any; // delete after astraRoutines migration
    astraRoutines: any[];
    astraRoutinePullback?: any;
    // processed: boolean;
    status: InstantRefundStatus;
    sandbox: boolean;
  };
  lastEmailReminder?: Date;
  // This object is not very consistent in the database
  pickup?: Record<string, any> & {
    /** @deprecated Only exists on older objects in the database from Feb-March 2024 */
    contact?: {
      email: string;
    };
    email?: string;
    phone?: string | null;
    pickupDate: string;
    pickupLocation: {
      packageLocation: PickupLocation;
      specialInstructions: string;
    };
    surveySent?: boolean;
  };
  pickupMetrics?: any;
  settlement?: Settlement;
  customerOrders?: Order[];
  customerReturns?: Return[];
  warnings?: ReturnWarning[];
  loopId: number;
  idempotencyKey?: string;
  expirationDate: Date;
  filedWithCarrier: Date | null;
}

export interface DiscountAllocation {
  discountedAmount: {
    amount: string;
  };
}

export interface ShopifyVariant {
  sku: string;
  product_id: number;
}

export interface NewItem {
  images: string[];
  price: string;
  variantTitle: string;
  variantId: string;
  title: string;
  shopifyVariant: ShopifyVariant;
  quantity: number;
  attributes?: { key: string; value: string }[];
}

export interface Totals {
  fee: number;
  refund: number;
  storeCredit: number;
  depositRefunded?: boolean;
  returnCollectionHoldAmount: number;
}

export interface Shipment {
  _shipment: EasypostShipment;
  _refund?: any;
  _refundError?: any;
  toAddress: Address;
  fromAddress: Address;
  parcel: {
    length?: number;
    width?: number;
    height?: number;
    weight: number;
  };
  isReturn: boolean;
  mode: "test" | "production";
  pickup: any;
  postage_label?: string;
  form_label?: string;
  shipmentGroupID?: string;
}

export interface ShipmentGroup {
  id: string;
  address: ReturnAddress;
}

export interface OrderSummary {
  name: string;
  number: number;
  order: string;
}

export interface Product {
  _id: string;
  exchange_for?: {
    images: string[];
    product_id: string;
    price: string;
    option1: string | null;
    option2: string | null;
    option3: string | null;
    variant_id: string;
    sku?: string;
    variant_title: string;
    product_title: string;
  };
  images: string[];
  variant_exists: boolean;
  strategy: string;
  status: string;
  quantity: number;
  grams: number;
  green_return: boolean;
  damaged: boolean;
  wrong_product: boolean;
  id: string;
  reason: string;
  condition: string;
  order: Order;
  product_id: string;
  price: string;
  price_adjustment?: string;
  merchant_adjustment?: string;
  sku: string;
  option1: string | null;
  option2: string | null;
  option3: string | null;
  variant_id: string;
  variant_title: string;
  product_title: string;
  line_item_id: string;
  notes: string;
  cancelled: boolean;
  rejectMessage?: string;
  manualReviewReason?: string;
  originalVariantOptions?: {
    strategy: string;
    exchange_for?: {
      images: string[];
      product_id: string;
      price: string;
      option1: string | null;
      option2: string | null;
      option3: string | null;
      variant_id: string;
      sku?: string;
      variant_title: string;
      product_title: string;
    };
    price_adjustment?: string;
    exchangeGroup?: ExchangeGroup;
    exchangeGroupItem?: ExchangeGroupItem;
  };
  multipleChoiceAnswers?: MultipleChoiceAnswer[];
  exchangeGroup?: ExchangeGroup;
  exchangeGroupItem?: ExchangeGroupItem;
  shipmentGroupID?: string;
  isVariantExchange: () => boolean;
}

export interface ExchangeGroupItem {
  productId: string;
  variantId: string;
  title: string;
  variantTitle: string;
  imageSrc: string;
}

export interface LineItemAnswers {
  lineItemId: string;
  answers: MultipleChoiceAnswer[];
}

export interface MultipleChoiceAnswer {
  result: string[];
  question: string;
  questionText: string;
  answer: string;
}

export interface ExchangeItem {
  variantId: string;
  title: string;
  variantTitle: string;
  images: [string];
  price: string;
  quantity: number;
  currencyCode: string;
  shopifyVariant: ShopifyVariant;
}

/**
 * Purchase value
 */
export function returnOriginalValue(return_: Return): number {
  return sumBy(return_.products || [], (product: Product) => +product.price);
}

export function returnOriginalPrice(return_: Return): number {
  return sumBy(
    return_.products || [],
    (product: Product) =>
      +(
        product.order.shopify.line_items.find(
          (lineItem: any) =>
            String(lineItem.id) === String(product.line_item_id),
        )?.price || product.price
      ),
  );
}

export function productTax(product: Product): number {
  let taxValue = 0.0;
  const shopifyLineItem = product.order.shopify.line_items.find(
    (lineItem: any) => String(lineItem.id) === String(product.line_item_id),
  );
  const taxLines = shopifyLineItem?.tax_lines || [];
  for (const taxLine of taxLines) {
    taxValue += parseFloat(taxLine.price) / (shopifyLineItem?.quantity || 1);
  }

  return taxValue;
}

export function returnTax(return_: Return): number {
  let taxValue = 0.0;
  const products = return_.products || [];
  for (const product of products) {
    taxValue += productTax(product);
  }
  return parseFloat(taxValue.toFixed(2));
}

export function returnDiscounts(return_: Return): number {
  let discountValue = 0.0;
  const products = return_.products || [];
  for (const product of products) {
    const lineItem = product.order.shopify.line_items.find(
      (lineItem: any) => String(lineItem.id) === String(product.line_item_id),
    );
    const discountAllocations = lineItem?.discount_allocations;
    if (discountAllocations) {
      for (const allocation of discountAllocations) {
        discountValue += parseFloat(allocation.amount) / lineItem.quantity;
      }
    }
  }
  return parseFloat(discountValue.toFixed(2));
}

export function returnAdjustment(return_: Return): number {
  return sumBy(
    return_?.products || [],
    (product: Product) => +product.price_adjustment! || 0,
  );
}

export function returnExchangeValue(return_: Return): number {
  return (
    sumBy(return_.products || [], (product) =>
      product.exchange_for ? +product.price || 0 : 0,
    ) +
    sumBy(return_.advancedExchangeItems || [], (product) => +product.price || 0)
  );
}

export function isDamaged(returnReason?: string | null): boolean {
  if (!returnReason) {
    return false;
  }
  const damaged =
    returnReason.toLowerCase().includes("damage") ||
    returnReason.toLowerCase().includes("broken") ||
    returnReason.toLowerCase().includes("defective");

  return damaged;
}

export function isWrongProduct(returnReason?: string | null): boolean {
  if (!returnReason) {
    return false;
  }
  // Some merchants differentiate between 'Purchased wrong product' and 'Received wrong product'.
  // We should not charge the merchant for 'Purchased wrong product'
  const wrongProduct =
    (returnReason.toLowerCase().includes("wrong product") ||
      returnReason.toLowerCase().includes("wrong item")) &&
    !(
      returnReason.toLowerCase().includes("purchased") ||
      returnReason.toLowerCase().includes("ordered")
    );

  return wrongProduct;
}
